import React from 'react'
import { Callout, Classes, Tag } from '@blueprintjs/core'

/**
 * DependencyCleanupInfo renders the dependency cleanup consequences
 * that will occur when deleting segmentation(s): weight removal,
 * formula cascades, dependents, and nets.
 *
 * @param {Object} props
 * @param {Object} props.dependencyCleanup - The dependency_cleanup object from the API.
 */
export function DependencyCleanupInfo({ dependencyCleanup }) {
  if (!dependencyCleanup) return null

  const {
    weight_removal,
    formula_cascade_deletions,
    dependents_cleanups,
    nets_cleanups,
  } = dependencyCleanup

  return (
    <>
      {/* Weight removal */}
      {weight_removal && (
        <Callout intent="warning" icon="warning-sign" style={{ marginBottom: 15 }}>
          Survey weight will be unset from variable{' '}
          <strong>{weight_removal.variable_name}</strong>
        </Callout>
      )}

      {/* Formula cascade deletions */}
      {formula_cascade_deletions.length > 0 && (
        <Callout intent="danger" icon="error" style={{ marginBottom: 15 }}>
          <strong>
            {formula_cascade_deletions.length} additional variable(s)
          </strong>{' '}
          will be permanently deleted because their formulas reference
          variables being deleted:
          <div
            style={{
              marginTop: 5,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 4,
            }}
          >
            {formula_cascade_deletions.map(v => (
              <Tag key={v.id} intent="danger" minimal>
                {v.name}
              </Tag>
            ))}
          </div>
        </Callout>
      )}

      {/* Dependents M2M cleanups */}
      {dependents_cleanups.length > 0 && (
        <Callout intent="warning" icon="info-sign" style={{ marginBottom: 15 }}>
          {dependents_cleanups.length} dependency link(s) will be removed
        </Callout>
      )}

      {/* Nets M2M cleanups */}
      {nets_cleanups.length > 0 && (
        <Callout intent="warning" icon="info-sign" style={{ marginBottom: 15 }}>
          {nets_cleanups.length} net relationship(s) will be removed
        </Callout>
      )}
    </>
  )
}

/**
 * DeletionConsequences renders warnings, slides to delete/modify,
 * and reports to delete.
 *
 * @param {Object} props
 * @param {Array} props.warnings - List of warning strings.
 * @param {Object} props.consequences - Object with slides_to_delete, slides_to_modify, reports_to_delete.
 */
export function DeletionConsequences({ warnings, consequences }) {
  return (
    <>
      {/* Warnings */}
      {warnings.length > 0 && (
        <Callout
          intent="warning"
          icon="warning-sign"
          style={{ marginBottom: 15 }}
        >
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {warnings.map((warning, idx) => (
              <li key={idx}>{warning}</li>
            ))}
          </ul>
        </Callout>
      )}

      {/* Slides to delete */}
      {consequences.slides_to_delete.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <h4 style={{ margin: '0 0 5px 0' }}>Slides to delete</h4>
          {consequences.slides_to_delete.map(slide => (
            <div
              key={slide.id}
              style={{
                marginBottom: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Tag intent="danger" minimal>
                {slide.name || `Slide ${slide.id}`}
              </Tag>
              <span className={Classes.TEXT_MUTED}>
                from &quot;{slide.report_name}&quot;
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Slides to modify */}
      {consequences.slides_to_modify.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <h4 style={{ margin: '0 0 5px 0' }}>Slides to modify</h4>
          {consequences.slides_to_modify.map(slide => (
            <div
              key={slide.id}
              style={{
                marginBottom: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Tag intent="warning" minimal>
                {slide.name || `Slide ${slide.id}`}
              </Tag>
              <span className={Classes.TEXT_MUTED}>
                remove from: {slide.usage.join(', ')}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Reports to delete */}
      {consequences.reports_to_delete.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <h4 style={{ margin: '0 0 5px 0' }}>Reports to delete</h4>
          {consequences.reports_to_delete.map(report => (
            <div key={report.id} style={{ marginBottom: 4 }}>
              <Tag intent="danger" minimal>
                {report.name}
              </Tag>
            </div>
          ))}
        </div>
      )}
    </>
  )
}
